import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Resolves the active vertical slug for the public landing page.
 *
 * Order of precedence:
 *   1. Explicit `?v=` query param (passed in as `verticalParam`) — wins always.
 *   2. The logged-in user's tenant `target_vertical_slug` (if any).
 *   3. `null` (neutral landing).
 *
 * Exported so the same resolution rule can be unit-tested in isolation
 * from the heavy Auth.tsx landing render tree.
 */
export function useTenantVertical(verticalParam: string | null): string | null {
  const [tenantVertical, setTenantVertical] = useState<string | null>(null);

  useEffect(() => {
    if (verticalParam) return;
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user || cancelled) return;

      const { data: m } = await supabase
        .from("tenant_memberships")
        .select("tenant_id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();
      if (!m?.tenant_id || cancelled) return;

      const { data: t } = await supabase
        .from("tenants")
        .select("target_vertical_slug")
        .eq("id", m.tenant_id)
        .maybeSingle();

      if (!cancelled && t?.target_vertical_slug) {
        setTenantVertical(t.target_vertical_slug);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [verticalParam]);

  return verticalParam ?? tenantVertical;
}
